"use client";

import React, { useState } from "react";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { ShieldCheck, Zap, Coins, ArrowRight, Star, Trophy } from "lucide-react";
function CasinoDirectoryContent() {
  const {
    posts,
    loading,
    error,
    setQuery
  } = usePosts();
  const [activeFilter, setActiveFilter] = useState("all");
  const handleFilterChange = (value: string) => {
    if (!value) return;
    setActiveFilter(value);
    if (value === "all") {
      setQuery({
        categories: undefined,
        tags: undefined
      });
    } else {
      setQuery({
        tags: value
      });
    }
  };
  return <div className="flex flex-col items-center w-full max-w-7xl mx-auto z-10 relative">
      {}
      <div className="w-full mb-12 flex justify-center">
        <ScrollArea className="w-full max-w-3xl whitespace-nowrap rounded-md">
          <div className="flex w-max space-x-4 p-1 mx-auto">
            <ToggleGroup type="single" value={activeFilter} onValueChange={handleFilterChange} className="gap-2">
              <ToggleGroupItem value="all" className="rounded-full px-6 py-2 font-default font-medium data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:shadow-md transition-all border border-border bg-secondary/50 hover:bg-secondary">
                All Tools
              </ToggleGroupItem>
              <ToggleGroupItem value="math" className="rounded-full px-6 py-2 font-default font-medium data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:shadow-md transition-all border border-border bg-secondary/50 hover:bg-secondary">
                Math
              </ToggleGroupItem>
              <ToggleGroupItem value="finance" className="rounded-full px-6 py-2 font-default font-medium data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:shadow-md transition-all border border-border bg-secondary/50 hover:bg-secondary">
                Finance
              </ToggleGroupItem>
              <ToggleGroupItem value="conversion" className="rounded-full px-6 py-2 font-default font-medium data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:shadow-md transition-all border border-border bg-secondary/50 hover:bg-secondary">
                Conversion
              </ToggleGroupItem>
              <ToggleGroupItem value="health" className="rounded-full px-6 py-2 font-default font-medium data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:shadow-md transition-all border border-border bg-secondary/50 hover:bg-secondary">
                Health
              </ToggleGroupItem>
            </ToggleGroup>
          </div>
        </ScrollArea>
      </div>

      {}
      {loading ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 w-full">
          {[...Array(6)].map((_, i) => <Card key={i} data-index={i} className="h-[400px] bg-card border-border animate-pulse rounded-xl" />)}
        </div> : error ? <div className="text-center py-12 text-destructive font-default">
          <p>Failed to load calculators. Please refresh the page.</p>
        </div> : posts.length === 0 ? <div className="text-center py-12 text-muted-foreground font-default">
          <p>No calculators found matching your search.</p>
        </div> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 w-full">
          {posts.map((post, i) => <Card key={post.id} data-index={i} className="relative overflow-hidden border-border bg-card backdrop-blur-none transition-all duration-300 hover:-translate-y-1 hover:border-primary hover:shadow-lg group flex flex-col">
              {}
              <div className="absolute top-0 left-0 bg-primary text-primary-foreground px-4 py-1.5 rounded-br-lg font-default font-bold text-lg z-10 flex items-center gap-1 shadow-sm">
                <Trophy className="w-4 h-4" /> Top
              </div>

              <CardHeader className="pt-10 pb-4 items-center border-b border-border relative">
                {}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 bg-primary/5 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                
                <div className="h-16 w-40 relative flex items-center justify-center mb-4 z-10">
                  <img src={post.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1600149/img/calculator_icon_placeholder.png"} alt={`${post.title} Icon`} className="max-h-full max-w-full object-contain" />
                </div>
                <h3 className="font-default text-xl font-bold text-center text-foreground">{post.title}</h3>
                <div className="flex items-center gap-1 text-primary mt-2">
                  {[...Array(5)].map((_, starIdx) => <Star key={starIdx} className="w-4 h-4 fill-current" />)}
                  <span className="text-xs text-muted-foreground ml-2 font-mono">Precision Verified</span>
                </div>
              </CardHeader>

              <CardContent className="py-6 flex flex-col gap-5 flex-grow">
                {}
                <div className="text-center p-4 rounded-lg bg-secondary border border-border relative overflow-hidden group-hover:border-primary/20 transition-colors">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary/5 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000 ease-in-out"></div>
                  <p className="text-xs text-muted-foreground mb-1.5 uppercase tracking-widest font-semibold flex items-center justify-center gap-1.5">
                    <Coins className="w-3 h-3 text-primary" /> Key Feature
                  </p>
                  <p className="font-default text-2xl font-bold text-foreground">
                    {post.customFields?.welcome_bonus || "Instant Results"}
                  </p>
                </div>

                {}
                <div className="flex flex-wrap justify-center gap-2 mt-auto">
                  <Badge variant="outline" className="bg-background border-border text-muted-foreground font-default py-1">
                    <ShieldCheck className="w-3 h-3 mr-1.5 text-primary" /> Accurate
                  </Badge>
                  <Badge variant="outline" className="bg-background border-border text-muted-foreground font-default py-1">
                    <Zap className="w-3 h-3 mr-1.5 text-primary" /> Fast Load
                  </Badge>
                  <Badge variant="outline" className="bg-background border-border text-muted-foreground font-default py-1">
                    SEO Optimized
                  </Badge>
                </div>
              </CardContent>

              <CardFooter className="pt-0 pb-6">
                <Link to={post.link || "#"} className="w-full">
                  <Button className="w-full font-default font-bold tracking-wide text-md h-12 group/btn relative overflow-hidden rounded-md">
                    <span className="relative z-10 flex items-center">
                      Open Calculator <ArrowRight className="w-4 h-4 ml-2 group-hover/btn:translate-x-1 transition-transform" />
                    </span>
                    <div className="absolute inset-0 bg-gradient-to-r from-primary/0 via-white/10 to-primary/0 translate-x-[-100%] group-hover/btn:translate-x-[100%] transition-transform duration-700 ease-in-out"></div>
                  </Button>
                </Link>
              </CardFooter>
            </Card>)}
        </div>}
    </div>;
}
export default function NoKycCasinos() {
  const wp_query_params = {
    post_type: "post",
    per_page: 9,
    orderby: "date",
    order: "desc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="225152" id="free-online-calculators-for-everything" className="dark bg-background text-foreground relative overflow-hidden py-20 md:py-32">
      {}
      <div className="absolute inset-0 bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1600149/img/geometric_grid_pattern.png')] bg-repeat opacity-[0.02] pointer-events-none"></div>
      
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-primary/5 rounded-full blur-[120px] pointer-events-none"></div>
      <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-primary/5 rounded-full blur-[100px] pointer-events-none"></div>

      <div className="container mx-auto px-4 relative z-10">
        {}
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <Badge variant="outline" className="mb-4 border-primary/30 text-primary bg-primary/5 px-4 py-1.5 font-default tracking-wider uppercase">
            Precision Tools
          </Badge>
          <h2 className="font-default text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-foreground tracking-tighter">
            Free Online Calculators for Everything
          </h2>
          <p className="text-lg text-muted-foreground font-default max-w-2xl mx-auto">
            Fast, accurate, and easy-to-use tools for math, finance, conversions, and more. Find exactly what you need in seconds.
          </p>
        </div>

        {}
        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 9,
        orderby: "date",
        order: "desc"
      }}>
          <CasinoDirectoryContent />
        </PostsProvider>
      </div>
    </section>;
}